/**
 * SlotEditor – modal for å sette tilgjengelighet på en dato.
 */
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X, Trash2, Users } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatFollowers } from "./CollabAccounts";

const COLLAB_TYPES = [
  { k: "sfs", label: "SFS" },
  { k: "mass", label: "MASS" },
  { k: "l4l", label: "L4L" },
  { k: "story_swap", label: "Story swap" },
];

const PLATFORMS = [
  "onlyfans", "fansly", "snapchat", "instagram", "x", "tiktok", "reddit", "bluesky", "other",
];

export default function SlotEditor({ date, existing, onClose, onSaved, onDelete }) {
  const { t } = useTranslation();
  const [isAvailable, setIsAvailable] = useState(existing?.is_available ?? true);
  const [types, setTypes] = useState(existing?.types || []);
  const [platforms, setPlatforms] = useState(existing?.platforms || []);
  const [accountIds, setAccountIds] = useState(existing?.account_ids || []);
  const [accounts, setAccounts] = useState([]);
  const [note, setNote] = useState(existing?.note || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/me/collab-accounts").then((r) => setAccounts(r.data?.results || [])).catch(() => {});
  }, []);

  const toggle = (arr, setArr, val) => {
    setArr(arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val]);
  };

  const toggleAccount = (accId) => {
    setAccountIds((cur) => cur.includes(accId) ? cur.filter((x) => x !== accId) : [...cur, accId]);
  };

  // Grupper kontoer pr plattform
  const accountsByPlatform = accounts.reduce((acc, a) => {
    (acc[a.platform] = acc[a.platform] || []).push(a);
    return acc;
  }, {});
  const platformsWithoutAccounts = PLATFORMS.filter((p) => !accountsByPlatform[p]);

  const save = async () => {
    setSaving(true);
    try {
      // Plattformer kommer fra valgte kontoer + manuelt valgte plattformer (uten kontoer)
      const platformsFromAccounts = Array.from(new Set(
        accounts.filter((a) => accountIds.includes(a.id)).map((a) => a.platform)
      ));
      const finalPlatforms = Array.from(new Set([...platformsFromAccounts, ...platforms]));
      await api.post("/collab/calendar/me", {
        date,
        is_available: isAvailable,
        types,
        platforms: finalPlatforms,
        account_ids: accountIds,
        note: note.trim() || null,
      });
      toast.success(t("collab.slot_saved"));
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/80 backdrop-blur-sm p-4 pt-24 pb-12 overflow-y-auto"
      onClick={onClose} data-testid="slot-editor">
      <div className="max-w-md w-full panel-tight p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute right-3 top-3 text-[var(--cream-faint)] hover:text-[var(--magenta)]">
          <X className="h-4 w-4" />
        </button>
        <h3 className="font-fraunces italic text-2xl text-[var(--cream)] mb-1">{t("collab.slot_title")}</h3>
        <div className="font-meta text-sm text-[var(--cream-soft)] mb-4">{date}</div>

        <div className="space-y-4">
          <div className="flex gap-2">
            <button
              onClick={() => setIsAvailable(true)}
              data-testid="slot-set-available"
              className={`flex-1 py-2 font-meta text-xs border ${isAvailable ? "border-[var(--gold)] text-[var(--gold)] bg-[var(--gold)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}
            >
              {t("collab.set_available")}
            </button>
            <button
              onClick={() => setIsAvailable(false)}
              data-testid="slot-set-busy"
              className={`flex-1 py-2 font-meta text-xs border ${!isAvailable ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}
            >
              {t("collab.set_busy")}
            </button>
          </div>

          {isAvailable && (
            <>
              <div>
                <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">{t("collab.types_label")}</div>
                <div className="flex flex-wrap gap-1">
                  {COLLAB_TYPES.map((opt) => (
                    <button
                      key={opt.k}
                      onClick={() => toggle(types, setTypes, opt.k)}
                      data-testid={`slot-type-${opt.k}`}
                      className={`px-3 py-1.5 font-meta text-[11px] border ${types.includes(opt.k) ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] mb-2">{t("collab.accounts_platforms_label")}</div>
                {accounts.length === 0 ? (
                  <p className="text-xs text-[var(--cream-faint)] mb-2">{t("collab.no_accounts_hint")}</p>
                ) : (
                  <div className="space-y-2 mb-2">
                    {Object.entries(accountsByPlatform).map(([plat, list]) => (
                      <div key={plat}>
                        <div className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)] mb-1">{plat.toUpperCase()}</div>
                        <div className="flex flex-wrap gap-1">
                          {list.map((a) => (
                            <button key={a.id} onClick={() => toggleAccount(a.id)}
                              data-testid={`slot-account-${a.id}`}
                              className={`inline-flex items-center gap-1.5 px-2 py-1 font-meta text-[11px] border ${accountIds.includes(a.id) ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}>
                              @{a.handle}
                              {a.label && <span className="opacity-60">·{a.label}</span>}
                              <span className="inline-flex items-center gap-0.5 opacity-70">
                                <Users className="h-2.5 w-2.5" />
                                {formatFollowers(a.follower_count)}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {platformsWithoutAccounts.length > 0 && (
                  <>
                    <div className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)] mt-3 mb-1">{t("collab.other_platforms_no_account")}</div>
                    <div className="flex flex-wrap gap-1">
                      {platformsWithoutAccounts.map((p) => (
                        <button
                          key={p}
                          onClick={() => toggle(platforms, setPlatforms, p)}
                          data-testid={`slot-platform-${p}`}
                          className={`px-3 py-1.5 font-meta text-[11px] border ${platforms.includes(p) ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}
                        >
                          {p.toUpperCase()}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </>
          )}

          {isAvailable && null}


          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.note_label")}
            </label>
            <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={200}
              placeholder={t("collab.note_placeholder")}
              className="input-editorial w-full" data-testid="slot-note" />
          </div>
        </div>

        <div className="flex gap-2 mt-6">
          <button onClick={save} disabled={saving} className="btn-magenta flex-1 disabled:opacity-40"
            data-testid="slot-save">
            {saving ? t("collab.saving") : t("collab.save")}
          </button>
          {existing && (
            <button onClick={onDelete} className="px-3 py-2 border border-red-400/40 text-red-300 hover:bg-red-400/10"
              data-testid="slot-delete">
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
